/**
 * Matches — the season, dynamic and feed-fed. Month nav over the full
 * fixture list; each fixture opens a detail page with kickoff facts, H2H
 * history, and (for played matches) the result and that match's Gauge.
 */
import { lazy, Suspense, useMemo, useState } from 'react';
import { DetailHeader, EmptyNote, Rule, StaleBadge } from '../components/chrome';
import { GaugePanel } from '../components/Gauge';
import { PredictionStrip } from '../components/PredictionStrip';
import { CommunityScore, currentCommunityReport, FreeDataAttribution } from '../components/FreeData';
import { ShareButton } from '../components/ShareButton';
import { WatchChip } from '../components/WatchChip';
import { MatchdayPassport } from '../components/MatchdayPassport';
import { CallReceipt } from '../components/CallReceipt';
import { MatchRibbon } from '../components/MatchRibbon';
import { H2HFeel } from '../components/Lore';
import { FEEDS, useFeed } from '../lib/feeds';
import type { FeedState } from '../lib/feeds';
import { resolveEntry } from '../lib/codex';
import { addToCalendar } from '../lib/ics';
import { canDownloadMatchdayCalendar } from '../lib/calendar-eligibility';
import { compShort, competitionBadge, confirmedKickoffIsFuture, dayOfWeek, fixtureDateParts, fixtureIsUpcoming, fixtureOrderTime, fixtureTimeLabel, monthOf } from '../lib/matchday';
import { matchLink } from '../lib/share';
import { arsenalScore } from '../lib/score';
import { useIsDesktop } from '../lib/useViewport';
import { matchRecordMatchesFixture, useMatchRecord } from '../lib/match-records';
import { useSupporterSignal, useTruthStatus } from '../lib/truth';
import type { ClubFeed, CommunityReport, Fixture, FixtureSource, FixturesFeed, H2HRecord, LoreEntry, MatchFeed, ResultLetter, VerifiedMatchRecord } from '../lib/types';

const MatchdayUtility = lazy(() => import('../components/MatchdayUtility').then((module) => ({ default: module.MatchdayUtility })));

const RES_CLASS: Record<ResultLetter, string> = { W: 'res-w', D: 'res-d', L: 'res-l' };

export function fixtureIsFreshLive(fixture: Fixture, fixtureFeedStale: boolean, match: Pick<MatchFeed, 'matchId' | 'phase'>, matchFeedStale: boolean): boolean {
  return fixture.status === 'live' && !fixtureFeedStale && !matchFeedStale && match.matchId === fixture.id && ['live', 'ht'].includes(match.phase);
}
const COVERAGE_LABEL: Record<VerifiedMatchRecord['verification'], string> = {
  score: 'Score verified', goals: 'Goals verified', full: 'Goals + selected events verified',
};

/** A row of small W/D/L marks — newest last, matching the squad form-strip read. */
const FormMarks = ({ form }: { form: ResultLetter[] }) => (
  <span className="form-marks" aria-label={`Recent form: ${form.join(', ')}`}>
    {form.map((r, i) => (
      <i key={i} className={`form-mark ${RES_CLASS[r]}`}>
        {r}
      </i>
    ))}
  </span>
);

function resultLetter(f: Fixture): ResultLetter | null {
  if (f.status !== 'ft') return null;
  const perspective = arsenalScore(f.score, f.venue, f.arsenalSide);
  if (!perspective) return null;
  if (perspective.ours > perspective.theirs) return 'W';
  if (perspective.ours < perspective.theirs) return 'L';
  return 'D';
}

// ── Detail ───────────────────────────────────────────────────────────────────
const MatchDetail = ({
  fixture,
  h2h,
  connectedEntryTitle,
  loreEntries,
  arsenalForm,
  onBack,
  backLabel,
  trail,
  onOpenThread,
  onOpenEntry,
  predictionOpen,
  threadOpen,
  gaugeOpen,
  matchRecord,
  matchRecordLoading,
  matchRecordOnline,
  matchRecordStored,
  previousFixture,
  nextFixture,
  matchPosition,
  matchCount,
  onOpenMatch,
  communityReport = null,
  fixtureSource,
  fixtureFeed,
}: {
  communityReport?: CommunityReport | null;
  fixtureSource?: FixtureSource | null;
  fixtureFeed: FeedState<FixturesFeed>;
  fixture: Fixture;
  h2h: H2HRecord | null;
  /**
   * The verified codex entry's title when this fixture's H2H carries a real,
   * resolved tie — drives the connected-history chip's honest label. Null when
   * there is no genuine link (most opponents) or the id doesn't resolve.
   */
  connectedEntryTitle: string | null;
  /** The loaded lore feed — folded into the emotional H2H for this opponent. */
  loreEntries: LoreEntry[];
  /** Arsenal's recent run (newest last), reused from the club feed. */
  arsenalForm: ResultLetter[];
  onBack: () => void;
  /** Names where Back returns to (the layer/tab beneath this one). */
  backLabel?: string;
  /** The full ancestor path for the "where am I" breadcrumb. */
  trail?: string[];
  onOpenThread: (matchId: string) => void;
  onOpenEntry: (id: string) => void;
  predictionOpen: boolean;
  threadOpen: boolean;
  gaugeOpen: boolean;
  matchRecord: VerifiedMatchRecord | null;
  matchRecordLoading: boolean;
  matchRecordOnline: boolean;
  matchRecordStored: boolean;
  previousFixture: Fixture | null;
  nextFixture: Fixture | null;
  matchPosition: number;
  matchCount: number;
  onOpenMatch: (matchId: string) => void;
}) => {
  const letter = resultLetter(fixture);
  const calendarAvailable = canDownloadMatchdayCalendar(fixture, fixtureFeed);
  const played = letter !== null;
  const signalActive = played && typeof fixture.kickoffUtc === 'string' && Date.now() < Date.parse(fixture.kickoffUtc) + 30 * 60 * 60_000;
  const supporterSignal = useSupporterSignal(fixture.id, played, signalActive);
  const perspective = arsenalScore(fixture.score, fixture.venue, fixture.arsenalSide);
  const oppForm = fixture.opponentForm ?? [];
  const showForm = !played && (arsenalForm.length > 0 || oppForm.length > 0);
  const wPct = h2h
    ? Math.round((h2h.record.w / Math.max(1, h2h.record.w + h2h.record.d + h2h.record.l)) * 100)
    : 0;

  return (
    <div className="screen detail-screen">
      <DetailHeader
        onBack={onBack}
        backLabel={backLabel ?? 'Matches'}
        trail={trail}
        right={
          <ShareButton
            ghost
            label="Share"
            moment={{
              title: 'COYG — match',
              text: (played
                ? `Arsenal ${perspective!.ours}–${perspective!.theirs} ${fixture.opponent} (${fixture.comp}).`
                : `Arsenal ${fixture.venue === 'A' ? 'away at' : 'vs'} ${fixture.opponent} — ${fixtureTimeLabel(fixture)}.`) + (fixtureSource?.provider === 'openligadb' ? ' Data: OpenLigaDB · ODbL 1.0 https://www.openligadb.de/lizenz' : ''),
              url: matchLink(fixture.id),
              card: {
                eyebrow: played ? `${fixture.comp} · FULL TIME` : fixture.comp,
                headline: played
                  ? `Arsenal ${perspective!.ours}–${perspective!.theirs} ${fixture.opponent}`
                  : `Arsenal ${fixture.venue === 'A' ? '@' : 'v'} ${fixture.opponent}`,
                detail: fixtureSource?.provider === 'openligadb' ? 'OpenLigaDB · ODbL 1.0' : played ? 'The verified match record and supporter Gauge' : fixtureTimeLabel(fixture),
                stamp: 'MATCHDAY · COYG.ORG',
              },
            }}
          />
        }
      />

      <nav className="match-pager" aria-label="Browse matches">
        <button
          type="button"
          className="match-pager__button match-pager__button--previous"
          disabled={!previousFixture}
          onClick={() => previousFixture && onOpenMatch(previousFixture.id)}
          aria-label={previousFixture ? `Previous match: ${previousFixture.opponent}` : 'No previous match'}
        >
          <span aria-hidden="true">←</span>
          <span>
            <small>Previous</small>
            <strong>{previousFixture?.opponent ?? 'Start of book'}</strong>
          </span>
        </button>
        <span className="match-pager__position" aria-live="polite">
          <small>Match</small>
          <strong>{matchPosition} / {matchCount}</strong>
        </span>
        <button
          type="button"
          className="match-pager__button match-pager__button--next"
          disabled={!nextFixture}
          onClick={() => nextFixture && onOpenMatch(nextFixture.id)}
          aria-label={nextFixture ? `Next match: ${nextFixture.opponent}` : 'No next match'}
        >
          <span>
            <small>Next</small>
            <strong>{nextFixture?.opponent ?? 'End of book'}</strong>
          </span>
          <span aria-hidden="true">→</span>
        </button>
      </nav>

      <section
        className={`detail-hero${played ? ` detail-hero--${RES_CLASS[letter!]}` : ' detail-hero--upcoming'}`}
      >
        {!played && (
          <span className="hero-ghost" aria-hidden="true">
            {fixture.opponentShort}
          </span>
        )}
        <div className="detail-hero-inner">
          <div className="detail-hero-top">
            <span className="detail-comp">{fixture.comp}</span>
            <WatchChip tv={fixture.tv} />
          </div>
          <h2 className="detail-tie">
            Arsenal <em>{fixture.venue === 'A' ? '@' : 'v'}</em> {fixture.opponent}
          </h2>
          <div className="detail-facts">
            <span>{fixture.ground ?? (fixture.venue === 'H' ? 'Emirates Stadium' : 'Away')}</span>
            {fixture.status === 'postponed' && <span className="detail-postponed">Postponed</span>}
            {fixture.statusQuality === 'overdue' && <span className="detail-postponed">Result unavailable · awaiting source confirmation</span>}
          </div>
          <div className="hero-cta">
            {played ? (
              <div className="detail-score">
                {perspective!.ours}
                <span>–</span>
                {perspective!.theirs}
              </div>
            ) : (
              <div className="detail-when">{fixtureTimeLabel(fixture)}</div>
            )}
            {!communityReport && !played && confirmedKickoffIsFuture(fixture) && fixture.status !== 'postponed' && (
              <div className="hero-actions">
                {!fixture.timeTbc && (
                  <button
                    type="button"
                    className="ghost-btn"
                    disabled={!calendarAvailable}
                    aria-describedby={!calendarAvailable ? `match-calendar-note-${fixture.id}` : undefined}
                    onClick={() => {
                      if (canDownloadMatchdayCalendar(fixture, fixtureFeed)) addToCalendar(fixture);
                    }}
                  >
                    Add to calendar
                  </button>
                )}
                {!calendarAvailable && <p className="match-action-note" id={`match-calendar-note-${fixture.id}`}>
                  {fixtureFeed.stale ? 'This is a saved fixture snapshot. Calendar download is paused until the dates are checked again.' : 'Calendar download needs a confirmed future kick-off from a fresh provider snapshot.'}{' '}
                  <a href="https://www.arsenal.com/fixtures/men/fixtures" target="_blank" rel="noopener noreferrer">Check Arsenal’s official calendar ↗</a>
                </p>}
                {threadOpen && <button className="ghost-btn" onClick={() => onOpenThread(fixture.id)}>
                  Match thread
                </button>}
                {fixture.timeTbc && <span className="match-action-note">Predictions and the match thread open when kick-off is confirmed.</span>}
              </div>
            )}
          </div>
        </div>
      </section>

      <FreeDataAttribution source={fixtureSource} />
      {communityReport && <CommunityScore report={communityReport} opponentShort={fixture.opponentShort} venue={fixture.venue} />}
      {!communityReport && !played && <Suspense fallback={null}><MatchdayUtility fixture={fixture} /></Suspense>}

      {played && (
        <>
          <Rule label="Match record" accent="var(--red-deep)" />
          <section
            className="verified-match-record"
            aria-label="Verified match record"
            role="status"
            aria-live="polite"
            aria-busy={matchRecordLoading}
          >
            {matchRecord ? (
              <>
                <div className={`verified-match-record__coverage verified-match-record__coverage--${matchRecord.verification}`}>
                  <strong>{COVERAGE_LABEL[matchRecord.verification]}</strong>
                  <span>{matchRecord.verification === 'score' ? 'Detailed goals are not published for this record' : matchRecord.verification === 'goals' ? 'Every published goal is accounted for' : 'Goals and additional match events are accounted for'}</span>
                </div>
                <p className="verified-match-record__summary">{matchRecord.summary}</p>
                <p className="verified-match-record__source">
                  {matchRecordLoading ? 'Checking for corrections…' : matchRecordOnline && matchRecordStored ? 'Correction checked' : matchRecordOnline ? 'Published match record' : 'Published match record · corrections could not be checked'}{' · '}{new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(matchRecord.checkedAt))}
                  {' · '}{[...new Set(matchRecord.sourceUrls ?? [matchRecord.sourceUrl])].map((sourceUrl, index) => <span key={sourceUrl}>{index ? ' · ' : ''}<a href={sourceUrl} target="_blank" rel="noopener noreferrer">{index ? `Official source ${index + 1}` : matchRecord.source} ↗</a></span>)}
                </p>
              </>
            ) : (
              <p className="verified-match-record__empty">
                {matchRecordLoading ? 'Checking the permanent record…' : 'Scorers and events have not been source-verified for this match yet.'}
              </p>
            )}
          </section>
          <MatchRibbon fixture={fixture} record={matchRecord} snapshots={supporterSignal.data?.snapshots ?? []} />
        </>
      )}

      {predictionOpen && !played && fixture.status !== 'postponed' && (
        <>
          <Rule label="Your call" accent="var(--gold)" />
          <PredictionStrip matchId={fixture.id} matchLabel={`Arsenal ${fixture.venue === 'A' ? '@' : 'v'} ${fixture.opponent}`} />
        </>
      )}
      <CallReceipt fixture={fixture} season={fixtureFeed.data.season} source={fixtureSource ?? undefined} stale={fixtureFeed.stale}/>

      {showForm && (
        <>
          <Rule label="Form going in" />
          <div className="form-going-in">
            {arsenalForm.length > 0 && (
              <div className="form-going-row">
                <span className="form-going-team">Arsenal</span>
                <FormMarks form={arsenalForm} />
              </div>
            )}
            {oppForm.length > 0 && (
              <div className="form-going-row">
                <span className="form-going-team">{fixture.opponent}</span>
                <FormMarks form={oppForm} />
              </div>
            )}
          </div>
        </>
      )}

      {h2h && (
        <>
          <Rule label="Head to head" accent="var(--gold)" />
          <div className="h2h">
            <div className="h2h-record">
              <div className="h2h-cell h2h-cell--w">
                <strong>{h2h.record.w}</strong>
                <em>Arsenal wins</em>
              </div>
              <div className="h2h-cell">
                <strong>{h2h.record.d}</strong>
                <em>Draws</em>
              </div>
              <div className="h2h-cell">
                <strong>{h2h.record.l}</strong>
                <em>{fixture.opponent} wins</em>
              </div>
            </div>
            <div className="h2h-bar">
              <progress className="h2h-bar-fill" value={wPct} max="100">{wPct}%</progress>
            </div>
          </div>

          <Rule label="Last five meetings" />
          <ul className="meetings">
            {h2h.recent.map((mt, i) => (
              <li key={i} className="meeting">
                <span className={`meeting-res ${RES_CLASS[mt.r]}`}>{mt.r}</span>
                <span className="meeting-score">{mt.score}</span>
                <span className="meeting-where">{mt.home ? 'Arsenal (H)' : `${fixture.opponent} (A)`}</span>
                <span className="meeting-date">{mt.date}</span>
              </li>
            ))}
          </ul>

          <Rule label="The read" accent="var(--gold)" />
          <blockquote className="oracle">
            <p>“{h2h.insight}”</p>
          </blockquote>

          {/* A single connected-history chip, shown ONLY when this opponent's
              H2H carries a genuine, verified codex tie that resolves. Reuses the
              unified gold honour-chip link affordance used across the app. */}
          {h2h.entryId && connectedEntryTitle && (
            <div className="connected-rail connected-rail--history">
              <button
                className="honour-chip honour-chip--link"
                onClick={() => onOpenEntry(h2h.entryId!)}
              >
                From the archive: {connectedEntryTitle} <span aria-hidden="true">→</span>
              </button>
            </div>
          )}
        </>
      )}

      {/* The proprietary emotional H2H — what no stats feed can give: how the
          North Bank feels about, and calls, this opponent. Honest empty state
          until we've actually played them in-app. */}
      <H2HFeel entries={loreEntries} opponent={fixture.opponent} />

      {played && gaugeOpen && (
        <>
          <Rule label="The gauge" accent="var(--red-deep)" />
          <GaugePanel
            matchId={fixture.id}
            matchLabel={`Arsenal ${perspective!.ours}–${perspective!.theirs} ${fixture.opponent}`}
            writable={gaugeOpen}
          />
        </>
      )}
    </div>
  );
};

// ── Row ──────────────────────────────────────────────────────────────────────
const FixtureRow = ({
  fixture,
  onOpen,
  next,
  selected,
  liveFresh,
}: {
  fixture: Fixture;
  onOpen: (f: Fixture) => void;
  next?: boolean;
  /** Desktop master-detail: marks the row whose detail is open in the side pane. */
  selected?: boolean;
  /** A live marker is allowed only while the fixture snapshot is current. */
  liveFresh?: boolean;
}) => {
  const { day, mon } = fixtureDateParts(fixture);
  const dow = dayOfWeek(fixture.kickoffUtc, fixture.kickoffDate);
  const letter = resultLetter(fixture);
  const perspective = arsenalScore(fixture.score, fixture.venue, fixture.arsenalSide);
  const cb = competitionBadge(fixture.comp);

  return (
    <button
      type="button"
      className={`fixture-row${next ? ' fixture-row--next' : ''}${selected ? ' fixture-row--selected' : ''}`}
      aria-current={selected ? 'page' : undefined}
      onClick={() => onOpen(fixture)}
    >
      {next && <span className="fixture-next-tag">Next</span>}
      <span className="fixture-date">
        {dow && <small className="fixture-dow">{dow}</small>}
        <strong>{day}</strong>
        <em>{mon}</em>
      </span>
      <span className="fixture-main">
        <span className="fixture-opp">
          {fixture.opponent}
          {fixture.status === 'postponed' && <i className="fixture-pp">PP</i>}
        </span>
        <span className="fixture-sub">
          <span
            className={`comp-badge comp-badge--${cb.kind}`}
            data-unavailable={!cb.available ? 'true' : undefined}
            title={cb.available ? undefined : 'Not available yet'}
          >
            {cb.short}{!cb.available ? ' · soon' : ''}
          </span>
          {' · '}{fixture.venue}
          {fixture.tv ? ` · ${fixture.tv}` : ''}
        </span>
      </span>
      {letter ? (
        <span className={`fixture-result ${RES_CLASS[letter]}`}>
          <span aria-label={`Arsenal ${perspective!.ours}, ${fixture.opponent} ${perspective!.theirs}`}>
            {perspective!.ours}–{perspective!.theirs}
          </span>
        </span>
      ) : fixture.statusQuality === 'overdue' ? (
        <span className="fixture-time-side">Result unavailable</span>
      ) : fixture.status === 'live' && liveFresh ? (
        <span className="fixture-live">
          <span className="live-dot pulse" /> Live
        </span>
      ) : fixture.status === 'live' ? (
        <span className="fixture-time-side">Last confirmed</span>
      ) : (
        <span className="fixture-time-side">{fixtureTimeLabel(fixture)}</span>
      )}
    </button>
  );
};

// ── Screen ───────────────────────────────────────────────────────────────────
export const MatchesScreen = ({
  openMatchId,
  onOpenMatch,
  onCloseMatch,
  onOpenThread,
  onOpenEntry,
  onOpenYear,
  backLabel,
  trail,
}: {
  openMatchId: string | null;
  onOpenMatch: (matchId: string) => void;
  onCloseMatch: () => void;
  onOpenThread: (matchId: string) => void;
  onOpenEntry: (id: string) => void;
  onOpenYear?: (year: number) => void;
  backLabel?: string;
  trail?: string[];
}) => {
  const feed = useFeed(FEEDS.fixtures);
  const data: FixturesFeed = feed.data;
  const club = useFeed(FEEDS.club);
  const lore = useFeed(FEEDS.lore);
  const activeMatch = useFeed(FEEDS.match);
  const codex = useFeed(FEEDS.codex);
  const squad = useFeed(FEEDS.squad);
  const truthStatus = useTruthStatus();
  const [month, setMonth] = useState('All');
  const isDesktop = useIsDesktop();
  const recordMatchId = openMatchId && data.fixtures.some((fixture) => fixture.id === openMatchId && fixture.status === 'ft') ? openMatchId : null;
  const matchRecord = useMatchRecord(recordMatchId);

  // Arsenal's recent run (newest last) from the club feed's all-comps run-in,
  // which is stored most-recent-first. Guard the legacy string[] shape.
  const arsenalForm = useMemo<ResultLetter[]>(() => {
    const last10 = (club.data as ClubFeed).lastSeason?.last10 ?? [];
    return last10
      .filter((g): g is { r: ResultLetter; comp: string; opp: string; home: boolean } =>
        !!g && typeof g === 'object' && 'r' in g,
      )
      .slice(0, 5)
      .map((g) => g.r)
      .reverse();
  }, [club.data]);

  // Month chips, derived from kickoff dates. With no fixtures at all there is
  // nothing to filter — suppress the lone "All" chip so it doesn't read as a
  // broken control above an empty list.
  const months = useMemo(() => {
    const seen: string[] = [];
    for (const f of data.fixtures) {
      const mo = monthOf(f.kickoffUtc, f.kickoffDate);
      if (mo && !seen.includes(mo)) seen.push(mo);
    }
    return seen.length === 0 ? [] : ['All', ...seen];
  }, [data.fixtures]);

  const noFixtures = data.fixtures.length === 0;
  const filtered =
    month === 'All' ? data.fixtures : data.fixtures.filter((f) => monthOf(f.kickoffUtc, f.kickoffDate) === month);
  const nextId =
    data.fixtures
      .filter((f) => f.status === 'scheduled' && fixtureIsUpcoming(f))
      .sort((a, b) => fixtureOrderTime(a) - fixtureOrderTime(b))[0]?.id ?? null;
  const authorisedCoverage = Boolean(truthStatus.data && truthStatus.data.coverage !== 'selected-matchbook');
  const rightsHold = Boolean(authorisedCoverage && (!truthStatus.data?.provider || truthStatus.data.provider.rights_status !== 'approved' || Date.parse(truthStatus.data.provider.review_expires_at) <= Date.now()));

  // The month filter + fixture list, shared by the plain list view and the
  // desktop master-detail's left column. openMatchId highlights the open row.
  const listBody = (
    <>
      <aside className="fixture-calendar-note" aria-label="Official fixture calendar">
        <span>
          <strong>{rightsHold ? 'Rights review required.' : truthStatus.data?.coverage === 'premier-league-complete' ? 'League calendar covered.' : truthStatus.data?.coverage === 'authorised-provider-snapshot' ? 'Authorised provider snapshot.' : 'Dates move.'}</strong>{' '}
          {rightsHold
            ? 'The last provider snapshot is on hold. Use Arsenal’s official calendar while COYG resolves the permission record.'
            : truthStatus.data?.coverage === 'premier-league-complete'
            ? `All 38 Premier League fixtures are supplied by ${truthStatus.data.provider?.label ?? 'the recorded provider'}; other competitions are unavailable in this provider view${truthStatus.data.contentUpdatedAt ? ` · source content ${new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(truthStatus.data.contentUpdatedAt))}` : ''}${truthStatus.data.lastCheckedAt ? ` · rechecked ${new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(truthStatus.data.lastCheckedAt))}` : ''}.`
            : truthStatus.data?.coverage === 'authorised-provider-snapshot'
              ? `${truthStatus.data.fixtureCount} men’s first-team records via ${truthStatus.data.provider?.label ?? 'the recorded provider'}${truthStatus.data.contentUpdatedAt ? ` · source content ${new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(truthStatus.data.contentUpdatedAt))}` : ''}${truthStatus.data.lastCheckedAt ? ` · rechecked ${new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(truthStatus.data.lastCheckedAt))}` : ''}. This is not a claim of season completeness.`
              : `COYG shows a selected matchbook, not the complete official list${truthStatus.data?.lastCheckedAt ? ` · rechecked ${new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium' }).format(new Date(truthStatus.data.lastCheckedAt))}` : truthStatus.data?.checkedAt ? ` · published ${new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium' }).format(new Date(truthStatus.data.checkedAt))}` : ''}.`}
        </span>
        <a href="https://www.arsenal.com/fixtures/men/fixtures" target="_blank" rel="noopener noreferrer">
          Arsenal fixture calendar ↗
        </a>
      </aside>
      <FreeDataAttribution source={data.source} />
      <div className="chip-strip" role="tablist" aria-label="Filter by month">
        {months.map((mo) => (
          <button
            key={mo}
            className={`chip${month === mo ? ' active' : ''}`}
            onClick={() => setMonth(mo)}
            role="tab"
            aria-selected={month === mo}
          >
            {mo}
          </button>
        ))}
      </div>

      {data.provisional && (
        <p className="empty-note provisional-note">
          Selected dates shown here — kick-off times, TV and cup ties can still move.
        </p>
      )}

      <div className="fixture-list">
        {filtered.map((f) => (
          <FixtureRow
            key={f.id}
            fixture={f}
            onOpen={(fx) => onOpenMatch(fx.id)}
            next={f.id === nextId}
            selected={f.id === openMatchId}
            liveFresh={fixtureIsFreshLive(f, feed.stale, activeMatch.data, activeMatch.stale)}
          />
        ))}
        {filtered.length === 0 &&
          (noFixtures ? (
            <EmptyNote>The fixture list lands when the season's confirmed.</EmptyNote>
          ) : (
            <EmptyNote>No fixtures in {month} yet.</EmptyNote>
          ))}
      </div>
    </>
  );

  const open = openMatchId ? data.fixtures.find((f) => f.id === openMatchId) ?? null : null;
  if (open) {
    const matchIndex = data.fixtures.findIndex((fixture) => fixture.id === open.id);
    const previousFixture = matchIndex > 0 ? data.fixtures[matchIndex - 1] : null;
    const nextFixture = matchIndex >= 0 && matchIndex < data.fixtures.length - 1 ? data.fixtures[matchIndex + 1] : null;
    const h2h = data.h2h?.[open.opponent] ?? null;
    // Resolve the connected codex tie's title for an honest chip label. Only a
    // real, resolving entry yields a title — an unknown id renders nothing.
    const resolved = h2h?.entryId ? resolveEntry(h2h.entryId, codex.data, squad.data) : null;
    const connectedEntryTitle = resolved?.kind === 'codex' ? resolved.entry.title : null;
    const isActive = open.id === activeMatch.data.matchId;
    const beforeKickoff = confirmedKickoffIsFuture(open);
    const nextAfterFullTime = activeMatch.data.phase === 'ft' && open.id === data.fixtures
      .filter((fixture): fixture is Fixture & { kickoffUtc: string } => fixture.kickoffUtc != null && Date.parse(fixture.kickoffUtc) > Date.parse(activeMatch.data.kickoffUtc ?? ''))
      .sort((a, b) => Date.parse(a.kickoffUtc) - Date.parse(b.kickoffUtc))[0]?.id;
    const report = isActive ? currentCommunityReport(activeMatch.data, activeMatch.stale) : null;
    const predictionOpen = !report && beforeKickoff && ((isActive && ['idle', 'pre'].includes(activeMatch.data.phase)) || nextAfterFullTime);
    const threadOpen = (isActive && (beforeKickoff || (!activeMatch.stale && ['live', 'ht'].includes(activeMatch.data.phase)))) || (nextAfterFullTime && beforeKickoff);
    const recentResultOpen = activeMatch.data.lastResult?.matchId === open.id && Boolean(activeMatch.data.lastResult.playedAt) &&
      Date.parse(activeMatch.data.lastResult.playedAt!) + 30 * 60 * 60_000 > Date.now();
    const gaugeOpen = (isActive && !activeMatch.stale && activeMatch.data.phase === 'ft') || recentResultOpen;
    const coherentMatchRecord = matchRecord.record && matchRecordMatchesFixture(matchRecord.record, open) ? matchRecord.record : null;
    const detail = (
      <MatchDetail
        fixture={open}
        communityReport={report}
        fixtureSource={data.source}
        fixtureFeed={feed}
        h2h={h2h}
        connectedEntryTitle={connectedEntryTitle}
        loreEntries={lore.data.entries}
        arsenalForm={arsenalForm}
        onBack={onCloseMatch}
        backLabel={backLabel}
        trail={trail}
        onOpenThread={onOpenThread}
        onOpenEntry={onOpenEntry}
        predictionOpen={predictionOpen}
        threadOpen={threadOpen}
        gaugeOpen={gaugeOpen}
        matchRecord={coherentMatchRecord}
        matchRecordLoading={matchRecord.loading}
        matchRecordOnline={matchRecord.online}
        matchRecordStored={matchRecord.stored}
        previousFixture={previousFixture}
        nextFixture={nextFixture}
        matchPosition={matchIndex + 1}
        matchCount={data.fixtures.length}
        onOpenMatch={onOpenMatch}
      />
    );
    // Mobile (narrow): unchanged — the detail replaces the list entirely.
    if (!isDesktop) return detail;
    // Desktop master-detail: fixture list on the left, open match on the right.
    return (
      <div className="screen matches-screen matches-screen--split">
        <div className="matches-split">
          <aside className="matches-split-list" aria-label="Fixture list">
            {listBody}
          </aside>
          <div className="matches-split-detail">{detail}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="screen matches-screen">
      <div className="screen-head">
        <div>
          <h1 className="screen-title">Matchbook</h1>
          <p className="screen-sub">Recent results and the next whistle</p>
        </div>
        <div className="screen-head-right">
          <span className="season-chip">{data.season}</span>
          <StaleBadge stale={feed.stale} asOf={feed.asOf} />
        </div>
      </div>

      <MatchdayPassport fixtures={data.fixtures} season={data.season} source={data.source} stale={feed.stale} onOpenMatch={onOpenMatch} onOpenYear={onOpenYear} />

      {listBody}
    </div>
  );
};
